import { Card, CardContent, Box, Typography, IconButton, Divider, Badge } from "@mui/joy";
import { Minus, Plus, Trash2 } from "lucide-react";
import { LaborSelection } from "../../../interfaces";

interface LaborCardProps {
    laborItem: any; // Using dynamic type for now as we transition to backend ILaborService
    selectedValue?: LaborSelection;
    onQuantityChange: (serviceId: string, event: "add" | "remove") => void;
    onRemove?: (serviceId: string) => void;
    priceRange?: { min: number; max: number };
}

export function LaborCard({ laborItem, selectedValue, onQuantityChange, onRemove, priceRange }: LaborCardProps) {
    const quantity = selectedValue?.quantity ?? 0;

    const handleQuantityChange = (event: "add" | "remove") => {
        onQuantityChange(laborItem.Id, event);
    };

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: "divider",
                zIndex: 2,
            }}
            variant="soft"
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {laborItem.Name}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                                ({laborItem.Unit})
                            </Typography>
                        </Box>
                        {laborItem.Description && (
                            <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.5 }}>
                                {laborItem.Description}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                borderRadius: "40px",
                                p: 0.2,
                                pb: 0.15,
                                alignItems: "center",
                                gap: 1,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <IconButton
                                variant="soft"
                                size="sm"
                                color="danger"
                                onClick={() => handleQuantityChange("remove")}
                                sx={{
                                    flex: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    "--IconButton-size": "32px",
                                    borderRadius: "50%",
                                }}
                            >
                                <Minus size={12} />
                            </IconButton>

                            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0 }} component={"span"}>
                                {quantity}
                            </Typography>
                            <Badge badgeContent={quantity} variant="soft" color="warning">
                                <IconButton
                                    variant="soft"
                                    color="success"
                                    size="sm"
                                    onClick={() => handleQuantityChange("add")}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        "--IconButton-size": "32px",
                                        borderRadius: "50%",
                                    }}
                                >
                                    <Plus size={12} />
                                </IconButton>
                            </Badge>
                        </Box>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {selectedValue?.unitPrice && selectedValue.unitPrice > 0
                                ? `$${selectedValue.unitPrice.toFixed(2)}`
                                : priceRange
                                  ? `$${priceRange.min.toFixed(2)} - $${priceRange.max.toFixed(2)}`
                                  : "-"}
                        </Typography>
                        <Typography level="body-xs">
                            {selectedValue?.unitPrice && selectedValue.unitPrice > 0
                                ? "Unit Price"
                                : "Unit Price Range"}
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" alignItems="center">
                        <IconButton
                            size="sm"
                            color="danger"
                            variant="soft"
                            onClick={() => onRemove?.(laborItem.Id)}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 6,
                                "--IconButton-size": "26px",
                            }}
                        >
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
